import { useEffect, useState, useCallback } from 'react';
import {
    Paper,
    Box,
} from '@mui/material';
import useStyles from '../../styles';
import { getExperimentalSites } from '../../../../api/expbDoc';
import PageHeading from '../../../../Components/PageHeading';
import ExperimentCustomTable from './ExperimentCustomTable';
import ExperimentDialog from './ExperimentDialog';
import { EXPB_DOC_CONSTANTS, SPLIT_CONFIG_TYPES } from '../../../../core/constants';
import { trimSiteDomain } from '../../../../utilities/helpers';

const { LIVE, PAUSED } = EXPB_DOC_CONSTANTS.SITE_STATUS;
const { NORMAL_SPLIT } = SPLIT_CONFIG_TYPES;
const { SITE_SYNC, SITE_UPDATE } = EXPB_DOC_CONSTANTS.BLOCKERS;

const createExperimentRow = (site) => {
    const activeExperiments = site.experiments.filter(exp => exp.status === LIVE || exp.status === PAUSED);

    if (activeExperiments.length > 0) {
        return activeExperiments.map(experiment => ({
            siteId: site.siteId,
            domain: trimSiteDomain(site.siteSplitData.siteDomain),
            entityTag: site.siteSplitData.tag,
            startedAt: experiment?.startedAt || 'NA',
            name: experiment.experimentName,
            description: experiment.comment,
            sync_block: (site.blockers || []).includes(SITE_SYNC),
            update_block: (site.blockers || []).includes(SITE_UPDATE),
            sectionLevelSplit: site.siteSplitData.sectionLevelSplit
        }));
    }

    const normalSplitRow = createNormalSplitRow(site);
    return normalSplitRow ? [normalSplitRow] : [];
};

const createNormalSplitRow = (site) => {
    const sectionLevelSplit = site.siteSplitData?.sectionLevelSplit;
    if (!sectionLevelSplit?.enabled || !sectionLevelSplit?.splits) return null;

    const [sectionId, sectionData] = Object.entries(sectionLevelSplit.splits).find(([, split]) => split.type === NORMAL_SPLIT) || [];
    if (!sectionId || !sectionData) return null;

    return {
        siteId: site.siteId,
        domain: trimSiteDomain(site.siteSplitData.siteDomain),
        entityTag: site.siteSplitData.tag,
        startedAt: 'NA',
        name: `Normal Split (${sectionId.split('-')[0]})`,
        description: sectionData.splitDescription || 'No description available',
        sync_block: (site.blockers || []).includes(SITE_SYNC),
        update_block: (site.blockers || []).includes(SITE_UPDATE),
        sectionLevelSplit
    };
};

const selectiveRolloutRow = (site) => ({
    siteId: site.siteId,
    domain: trimSiteDomain(site.siteSplitData.siteDomain),
    entityTag: site.siteSplitData.tag,
    startedAt: 'NA',
    name: site.siteSplitData.siteDomain,
    description: 'No description available',
    sync_block: (site.blockers || []).includes(SITE_SYNC),
    update_block: (site.blockers || []).includes(SITE_UPDATE),
    sectionLevelSplit: {}
});

function ExperimentTable() {
    const [experimentData, setExperimentData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);

    const classes = useStyles();

    const TABLE_HEADER = [
        { id: 'siteId', label: 'Site ID', sortable: false, type: 'number' },
        { id: 'domain', label: 'Site Domain', sortable: true, type: 'string' },
        { id: 'entityTag', label: 'Tag', sortable: false, type: 'string' },
        { id: 'startedAt', label: 'Start Date', sortable: true, type: 'date' },
        { id: 'name', label: 'Experiment Name', sortable: false, type: 'string' },
        { id: 'description', label: 'Experiment Description', sortable: false, type: 'string' },
        { id: 'sync_block', label: 'Sync Blocked', sortable: false, type: 'boolean' },
        { id: 'update_block', label: 'Update Blocked', sortable: false, type: 'boolean' }
    ];

    /**
     * This function transforms the data from the expb doc and sites doc to the table suitable format
     * @param {*} expDataPayload : `{experimentalSites, normalSplitSites, selectiveRolloutSites}`
     */
    const transformExpData = useCallback(expDataPayload => {
        const { experimentalSites, normalSplitSites, selectiveRolloutSites } =
            expDataPayload?.allSites || {};

        const processExperimentalSites =
            experimentalSites?.flatMap(site => createExperimentRow(site)) || [];

        const processNormalSplitSites =
            normalSplitSites?.map(site => createNormalSplitRow(site)).filter(Boolean) || [];

        const processSelectiveRolloutSites =
            selectiveRolloutSites?.map(site => selectiveRolloutRow(site)) || [];

        const finalExperimentData = [
            ...processExperimentalSites,
            ...processNormalSplitSites,
            ...processSelectiveRolloutSites
        ];

        setExperimentData(finalExperimentData);
    }, []);

    useEffect(() => {
        async function fetchExperimentData() {
            try {
                setLoading(true);

                const { data: expbDocData } = await getExperimentalSites();
                const payload = expbDocData?.data;

                transformExpData(payload);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching experiment data:', error);
            } finally {
                setLoading(false);
            }
        }

        fetchExperimentData();
    }, [transformExpData]);

    const handleRowClick = rowData => {
        setSelectedRowData(rowData);
        setModalOpen(true);
    };

    const handleSiteIdClick = (siteId, event) => {
        event.stopPropagation();
        window.open(`/update-config/${siteId}`, '_blank');
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedRowData(null);
    };

    return (
        <Paper className={classes.dashboardMainContent}>
            <Box className={classes.pageHeadingAndButtonsContainer}>
                <PageHeading headingName="Active Experiments" headingPath={['Dashboard', 'Active Experiments']} />
            </Box>
            <Box className={classes.customTableWrapper} sx={{ maxHeight: '85%', marginTop: '0.5rem', }}>
                <ExperimentCustomTable
                    experimentData={experimentData}
                    sitesLoaded={!loading}
                    header={TABLE_HEADER}
                    rowsPerPageInit={5}
                    rowsPerPageOptions={[5, 10, 20]}
                    handleRowClick={handleRowClick}
                    handleSiteIdClick={handleSiteIdClick}
                    defaultKey={"startedAt"}
                />
            </Box>

            {/* Split Data Modal */}
            {modalOpen && (
                <ExperimentDialog
                    selectedRowData={selectedRowData}
                    modalOpen={modalOpen}
                    handleModalClose={handleModalClose}
                />
            )}
        </Paper>
    );
}

export default ExperimentTable;
